
import { defineComponent, onMounted, computed, ref } from "vue";
import { useRoute } from "vue-router";

import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import SyncTransactionModal from "@/components/modals/general/SyncTransactionModal.vue";
import UploadTransactionModal from "@/components/modals/general/UploadTransactionModal.vue";
import NewChargebackModal from "@/components/modals/general/NewChargebackModal.vue";
import UploadChargebackModal from "@/components/modals/general/UploadChargebackModal.vue";
import SyncChargebackModal from "@/components/modals/general/SyncChargebackModal.vue";
import MerchantSettingsModal from "@/components/modals/general/MerchantSettingsModal.vue";
import EditMerchantModal from "@/components/modals/general/EditMerchantModal.vue";
import MerchantsActionToolbar from "@/layout/toolbar/MerchantsActionToolbar.vue";
import EPayAuthModal from "@/components/modals/general/EPayAuthModal.vue";
import EPaySaleModal from "@/components/modals/general/EPaySaleModal.vue";

interface Merchant {
  dba_name: string;
  processor_id: string;
  created: string;
  modified: string;
  group_id: string;
  customer_id: string;
  merchant_id: string;
  mid_number: string;
  merchant_type: string;
  // eslint-disable-next-line
  merchantDetails: any;
}

export default defineComponent({
  name: "merchants-details",
  components: {
    SyncTransactionModal,
    UploadTransactionModal,
    EPayAuthModal,
    EPaySaleModal,
    NewChargebackModal,
    UploadChargebackModal,
    SyncChargebackModal,
    MerchantSettingsModal,
    EditMerchantModal,
    MerchantsActionToolbar,
  },
  setup() {
    const merchant = ref<Merchant>({
      dba_name: "",
      processor_id: "",
      created: "",
      modified: "",
      group_id: "",
      merchant_id: "",
      customer_id: "",
      mid_number: "",
      merchant_type: "",
      merchantDetails: {},
    });

    onMounted(async () => {
      setCurrentPageTitle("Merchant Account Details");

      setTimeout(async () => {
        getMerchant();
      }, 500);
    });

    const route = useRoute();
    const getMerchantId = computed(() => {
      return route.params.id;
    });
    const totalVoids = ref(0);
    const processorName = ref("");
    const totalRefunds = ref(0);
    const totalCaptures = ref(0);
    const totalSales = ref(0);
    const totalAuths = ref(0);
    const totalChargebacks = ref(0);
    const totalFraudChargebacks = ref(0);
    const fraudCategory = ref("");
    const processorId = ref("");
    const totalConsumerChargebacks = ref(0);
    const consumerCategory = ref("");
    const totalProcessingChargebacks = ref(0);
    const processingCategory = ref("");
    const totalAuthorizationChargebacks = ref(0);
    const authorizationCategory = ref("");
    const cbratio = ref("0");
    const nameMonth = ref("");

    const getMerchant = async () => {
      const { data } = await ApiService.get(
        `merchants/accounts?merchant_id=${getMerchantId.value}`
      );
      const merchant_detail = data.data[0];
      merchant_detail.checkoutPage = JSON.parse(merchant_detail.checkoutPage);
      merchant_detail.merchantDetails = JSON.parse(
        merchant_detail.merchantDetails
      );
      merchant_detail.descriptorVariations = JSON.parse(
        merchant_detail.descriptorVariations
      );
      merchant.value = merchant_detail;
      processorId.value = data.data[0].processor_id;
      const d = new Date();
      const monthName = d.toLocaleString("default", { month: "long" });

      let res = await ApiService.get(
        `reports/chargebacks/incoming.monthly?merchant_id=${getMerchantId.value}&month=${monthName}`
      );
      res = await ApiService.get(
        `processors/isos/${processorId.value}?fields=name`
      );
      if (res.data.data) {
        processorName.value = String(res.data.data[0].name);
      }
      if (res.data.data) {
        totalChargebacks.value = Number(res.data.data[0].count);
      }
      res = await ApiService.get(
        `reports/chargebacks/incoming.reasons.categories?merchant_id=${getMerchantId.value}&month=${monthName}&reason_category=Fraud`
      );
      if (res.data.data) {
        totalFraudChargebacks.value = Number(res.data.data[0].count);
        fraudCategory.value = res.data.data[0].reason_category;
      }
      if (fraudCategory.value == "") {
        fraudCategory.value = "Fraud";
      }

      res = await ApiService.get(
        `reports/chargebacks/incoming.reasons.categories?merchant_id=${getMerchantId.value}&month=${monthName}&reason_category=Processing Error`
      );
      if (res.data.data) {
        totalProcessingChargebacks.value = Number(res.data.data[0].count);
        processingCategory.value = res.data.data[0].reason_category;
      }
      if (processingCategory.value == "") {
        processingCategory.value = "Processing Errors";
      }
      res = await ApiService.get(
        `reports/chargebacks/incoming.reasons.categories?merchant_id=${getMerchantId.value}&month=${monthName}&reason_category=Consumer Dispute`
      );
      if (res.data.data) {
        totalConsumerChargebacks.value = Number(res.data.data[0].count);
        consumerCategory.value = res.data.data[0].reason_category;
      }
      if (consumerCategory.value == "") {
        consumerCategory.value = "Consumer Dispute";
      }
      res = await ApiService.get(
        `reports/chargebacks/incoming.reasons.categories?merchant_id=${getMerchantId.value}&month=${monthName}&reason_category=Authorization`
      );
      if (res.data.data) {
        totalAuthorizationChargebacks.value = Number(res.data.data[0].count);
        authorizationCategory.value = res.data.data[0].reason_category;
        nameMonth.value = res.data.data[0].month;
      }
      if (authorizationCategory.value == "") {
        authorizationCategory.value = "Authorization";
      }
      res = await ApiService.get(
        `reports/transactions/transactions.monthly?merchant_id=${getMerchantId.value}&action_type=auth&month=${monthName}`
      );
      if (res.data.data) {
        totalAuths.value = Number(res.data.data[0].count);
      }
      res = await ApiService.get(
        `reports/transactions/transactions.monthly?merchant_id=${getMerchantId.value}&action_type=capture&month=${monthName}`
      );
      if (res.data.data) {
        totalCaptures.value = Number(res.data.data[0].count);
      }
      res = await ApiService.get(
        `reports/transactions/transactions.monthly?merchant_id=${getMerchantId.value}&action_type=refund&month=${monthName}`
      );
      if (res.data.data) {
        totalRefunds.value = Number(res.data.data[0].count);
      }
      res = await ApiService.get(
        `reports/transactions/transactions.monthly?merchant_id=${getMerchantId.value}&action_type=void&month=${monthName}`
      );
      if (res.data.data) {
        totalVoids.value = Number(res.data.data[0].count);
      }

      res = await ApiService.get(
        `reports/transactions/transactions.overview?merchant_id=${getMerchantId.value}&action_type=sale&month=${monthName}`
      );
      if (res.data.data) {
        totalSales.value = Number(res.data.data[0].count);
      }

      if (totalSales.value === 0) {
        cbratio.value = "0";
      } else {
        cbratio.value = (
          (totalChargebacks.value / totalSales.value) *
          100
        ).toFixed(2);
      }
    };

    return {
      getMerchantId,
      merchant,
      totalSales,
      totalAuths,
      totalCaptures,
      totalVoids,
      processorName,
      totalRefunds,
      totalChargebacks,
      totalFraudChargebacks,
      fraudCategory,
      totalProcessingChargebacks,
      consumerCategory,
      totalConsumerChargebacks,
      authorizationCategory,
      processingCategory,
      processorId,
      totalAuthorizationChargebacks,
      cbratio,
      nameMonth,
      getMerchant,
    };
  },
});
